import React, { useState } from 'react';
import { Box } from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import type { GridColDef } from '@mui/x-data-grid';

/**
 * Draggable column header (GP-1121, GP-1221). The community MUI DataGrid has no
 * built-in column reordering (that's a Pro feature), so we make each header a
 * native HTML5 drag source/target: drag one header onto another to move it
 * before that column. The dragged field id is shared via a ref rather than
 * dataTransfer so the drop can read it synchronously. Sorting still works — a
 * plain click never starts a drag, and we stop propagation only on the drag
 * gestures.
 *
 * Extracted from QueueView so the queue grids AND the Reimbursement Tracker (and
 * any future grid) share ONE implementation — the codebase's "one source of
 * truth" rule. Pair with `usePersistedDataGrid` (columnOrder/onColumnOrderChange)
 * to persist the order per user.
 */
export const DraggableColumnHeader: React.FC<{
  field: string;
  label: string;
  draggedFieldRef: React.MutableRefObject<string | null>;
  onReorder: (fromField: string, toField: string) => void;
}> = ({ field, label, draggedFieldRef, onReorder }) => {
  const [isOver, setIsOver] = useState(false);
  return (
    <Box
      draggable
      onDragStart={(e) => {
        draggedFieldRef.current = field;
        e.dataTransfer.effectAllowed = 'move';
        e.stopPropagation();
      }}
      onDragEnd={() => {
        draggedFieldRef.current = null;
        setIsOver(false);
      }}
      onDragOver={(e) => {
        const dragged = draggedFieldRef.current;
        if (dragged && dragged !== field) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (!isOver) setIsOver(true);
        }
      }}
      onDragLeave={(e) => {
        // dragleave ALSO fires when the cursor crosses onto a child (the drag
        // icon or the label span), which would clear and immediately re-set the
        // drop indicator → flicker. Only clear it when the cursor truly leaves
        // this header box (relatedTarget is null or outside it). (Sentry review, LOW.)
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        const from = draggedFieldRef.current;
        draggedFieldRef.current = null;
        if (from && from !== field) onReorder(from, field);
      }}
      title="Drag to reorder column"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        width: '100%',
        height: '100%',
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        boxShadow: isOver ? (theme) => `inset 2px 0 0 0 ${theme.palette.primary.main}` : 'none',
      }}
    >
      <DragIndicatorIcon
        sx={{ fontSize: '0.9rem', color: 'text.disabled', flexShrink: 0 }}
      />
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 600,
          fontSize: '0.8125rem',
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

/**
 * GP-1121/GP-1221: the reorderable column field ids in the user's saved order.
 * `pinnedFields` (e.g. a sticky `actions` column) are excluded and never
 * reordered. Fields present in `columnOrder` come first (in that order); any
 * column NOT in the saved order keeps its natural position, appended after — so
 * a stale saved order never hides a newly-added column.
 *
 * Pure (no hooks) so BOTH the memoized column build and the reorder handler
 * derive the current order from the same source of truth (columns + columnOrder),
 * instead of the handler reading the built ordered columns. That removes the
 * circular useMemo↔useCallback dependency the earlier version had.
 */
/**
 * The ONE ordering rule for a user's saved column order, over plain field ids:
 * fields named in the saved order come first, in that order, each once (a
 * repeated or unknown name is ignored); every other field keeps its natural
 * position after them. Shared by the DataGrid path below and the plain-table
 * `usePersistedTableColumns` (PRX-80 Task 14).
 */
export const arrangeFieldOrder = (fields: string[], columnOrder: string[]): string[] => {
  if (columnOrder.length === 0) return fields;
  const remaining = new Set(fields);
  const inSaved: string[] = [];
  for (const field of columnOrder) {
    if (remaining.has(field)) {
      inSaved.push(field);
      remaining.delete(field);
    }
  }
  const leftovers = fields.filter((f) => remaining.has(f));
  return [...inSaved, ...leftovers];
};

export const arrangeReorderableFields = (
  columns: GridColDef[],
  columnOrder: string[],
  pinnedFields: string[] = ['actions'],
): string[] => {
  const pinned = new Set(pinnedFields);
  const reorderable = columns.filter((c) => !pinned.has(c.field)).map((c) => c.field);
  return arrangeFieldOrder(reorderable, columnOrder);
};
